import { api } from './client';

export interface ServerMetrics {
  server_id: string;
  server_name: string;
  ip: string;
  status: 'online' | 'offline' | 'degraded';
  uptime: number;
  
  app_version: string;
  app_pid: number;
  app_memory_mb: number;
  app_cpu_percent: number;
  
  system_memory_total_mb: number;
  system_memory_used_mb: number;
  system_memory_percent: number;
  system_cpu_percent: number;
  system_load_1m: number;
  system_load_5m: number;
  system_load_15m: number;
  system_disk_used_gb: number;
  system_disk_total_gb: number;
  system_disk_percent: number;
  system_disk_iops: number;
  
  db_status: string;
  db_connections: number;
  db_max_connections: number;
  db_idle_connections: number;
  
  redis_status: string;
  redis_connected_clients: number;
  redis_used_memory_mb: number;
  redis_keys: number;
  
  mqtt_status: string;

  nginx_status: string;
  nginx_active_connections: number;
  nginx_accepts: number;
  nginx_handled: number;
  nginx_requests: number;
  nginx_reading: number;
  nginx_writing: number;
  nginx_waiting: number;
  
  alerts: Alert[];
  last_updated: string;
}

export interface Alert {
  id: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  type: string;
  message: string;
  details?: string;
  timestamp: string;
  acknowledged: boolean;
}

export interface SystemOverview {
  status: 'healthy' | 'degraded' | 'unhealthy';
  server_name: string;
  uptime: number;
  cpu_percent: number;
  memory_percent: number;
  disk_percent: number;
  db_status: string;
  db_connections: number;
  redis_status: string;
  redis_keys: number;
  redis_memory_mb: number;
  mqtt_status: string;
  nginx_status: string;
  nginx_active_connections: number;
  nginx_requests: number;
  alerts_count: number;
  critical_alerts: number;
  recent_alerts: Alert[];
  last_updated: string;
}

export interface MetricsHistory {
  timestamps: number[];
  cpu: number[];
  memory: number[];
  disk: number[];
  iops: number[];
}

export interface DatabaseMetrics {
  status: string;
  open_connections: number;
  in_use: number;
  idle: number;
  max_open: number;
  wait_count: number;
  wait_duration_ms: number;
}

export interface RedisMetrics {
  status: string;
  connected_clients: number;
  used_memory_mb: number;
  max_memory_mb: number;
  keys: number;
  hit_rate: number;
  error?: string;
}

export interface NginxMetrics {
  status: string;
  active_connections: number;
  accepts: number;
  handled: number;
  requests: number;
  reading: number;
  writing: number;
  waiting: number;
}

export const monitoringApi = {
  getOverview: async (): Promise<SystemOverview> => {
    const response = await api.get('/admin/monitoring/overview');
    return response.data;
  },

  getServerMetrics: async (): Promise<ServerMetrics> => {
    const response = await api.get('/admin/monitoring/server');
    return response.data;
  },

  getMetricsHistory: async (period: '1h' | '6h' | '24h' = '1h'): Promise<MetricsHistory> => {
    const response = await api.get('/admin/monitoring/history', { params: { period } });
    return response.data;
  },

  getDatabaseMetrics: async (): Promise<DatabaseMetrics> => {
    const response = await api.get('/admin/monitoring/database');
    return response.data;
  },

  getRedisMetrics: async (): Promise<RedisMetrics> => {
    const response = await api.get('/admin/monitoring/redis');
    return response.data;
  },

  getNginxMetrics: async (): Promise<NginxMetrics> => {
    const response = await api.get('/admin/monitoring/nginx');
    return response.data;
  },
};
